import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import JSZip from 'jszip';
import {
  EnhancedProjectFile,
  ProjectFile,
  FileFilters,
  getDefaultFilters,
  formatFileSize,
  UploaderInfo,
} from '../../types/file.types';
import { getProjectFiles, getMoldFiles, deleteProjectFile, deleteMoldFile, toggleFileFavorite } from '../../services/file.service';
import { getProjectById } from '../../services/project.service';
import { getMoldById, getMoldsByProjectId } from '../../services/mold.service';
import { userService } from '../../services/user.service';
import { Mold } from '../../types';
import FileToolbar from './FileToolbar';
import { ViewMode } from './FileViewModeToggle';
import FileGridView from './FileGridView';
import FileListView from './FileListView';
import FileTimelineView from './FileTimelineView';
import FileUploadZone from './FileUploadZone';
import FileFilterPanel from './FileFilterPanel';
import FileDetailPanel from './FileDetailPanel';
import { updateFileInfo, FileUpdateData } from '../../services/file.service';

/**
 * FileManagerPage Props
 */
interface FileManagerPageProps {
  /** 부모 타입 (프로젝트 또는 금형) */
  parentType: 'project' | 'mold';
}

/**
 * 파일 관리 메인 페이지
 * 
 * @description
 * 프로젝트 또는 금형의 파일 목록을 표시하고 관리하는 메인 컴포넌트
 * 
 * @features
 * - 파일 목록 표시 (그리드/리스트/타임라인 뷰)
 * - 파일 검색 및 필터링 (카테고리, 업로더, 크기, 태그, 즐겨찾기)
 * - 파일 업로드
 * - 파일 선택 및 일괄 작업
 * - 파일 다운로드/삭제
 * 
 * @performance
 * - useMemo로 필터링/정렬 최적화
 * - useCallback으로 이벤트 핸들러 메모이제이션
 * - React.memo로 자식 컴포넌트 최적화
 */
const FileManagerPage: React.FC<FileManagerPageProps> = ({ parentType }) => {
  const { projectId, id } = useParams<{ projectId: string; id: string }>();
  const navigate = useNavigate();
  
  const parentId = parentType === 'project' ? projectId : id;
  
  const [files, setFiles] = useState<EnhancedProjectFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parentName, setParentName] = useState<string>('');
  const [users, setUsers] = useState<UploaderInfo[]>([]); // 사용자 목록
  
  // UI 상태
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<EnhancedProjectFile | null>(null); // 상세 패널용
  const [showUpload, setShowUpload] = useState(false);
  const [showFilter, setShowFilter] = useState(false); // 필터 패널 표시 상태
  const [filters, setFilters] = useState<FileFilters>(getDefaultFilters());

  // 금형 파일 통합 뷰 관련 상태
  const [showMoldFiles, setShowMoldFiles] = useState<boolean>(() => {
    // 로컬 스토리지에서 읽기 (기본값: true)
    const saved = localStorage.getItem('fileManager.showMoldFiles');
    return saved !== null ? saved === 'true' : true;
  });
  const [relatedMolds, setRelatedMolds] = useState<Mold[]>([]);
  const [moldFileCounts, setMoldFileCounts] = useState<Record<string, number>>({});
  const [moldFiles, setMoldFiles] = useState<Record<string, EnhancedProjectFile[]>>({});
  const [loadingMolds, setLoadingMolds] = useState(false);

  // 데이터 로딩
  useEffect(() => {
    if (!parentId) {
      setError('유효하지 않은 ID입니다.');
      setLoading(false);
      return;
    }

    loadFiles();
  }, [parentId]);

  // 사용자 목록 로딩
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await userService.getAllUsers();
        const uploaderList: UploaderInfo[] = allUsers.map(user => ({
          id: user.uid || user.id || '',
          name: user.name || user.displayName || user.email || 'Unknown',
          email: user.email || '',
        }));
        setUsers(uploaderList);
      } catch (error) {
        console.error('사용자 목록 로드 실패:', error);
        // 에러가 나도 파일 관리는 계속 작동
      }
    };
    
    loadUsers();
  }, []);

  // 필터/검색 변경 시 선택 초기화
  useEffect(() => {
    setSelectedFiles([]);
  }, [filters, searchTerm]);

  // showMoldFiles 변경 시 로컬 스토리지 저장
  useEffect(() => {
    localStorage.setItem('fileManager.showMoldFiles', showMoldFiles.toString());
  }, [showMoldFiles]);

  // 프로젝트 타입이고 금형 파일 표시 옵션이 켜져있을 때 관련 금형 로드
  useEffect(() => {
    if (parentType === 'project' && showMoldFiles && parentId) {
      loadRelatedMolds();
    }
  }, [parentType, showMoldFiles, parentId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 파일 로딩
      if (!parentId) {
        throw new Error('유효하지 않은 ID입니다.');
      }

      let loadedFiles: ProjectFile[];
      if (parentType === 'project') {
        // 프로젝트 정보 및 파일 로드
        const project = await getProjectById(parentId);
        if (project) {
          setParentName(`${project.projectId}_${project.projectName}`);
        }
        loadedFiles = await getProjectFiles(parentId);
      } else {
        // 금형 정보 및 파일 로드
        const mold = await getMoldById(parentId);
        if (mold) {
          setParentName(`${mold.moldNumber}_${mold.moldName || ''}`);
        }
        loadedFiles = await getMoldFiles(parentId);
      }
      
      setFiles(loadedFiles as EnhancedProjectFile[]);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('파일 목록을 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };

  /**
   * 프로젝트에 연결된 금형 및 파일 개수 로드
   */
  const loadRelatedMolds = async () => {
    if (!parentId) return;

    try {
      setLoadingMolds(true);
      const molds = await getMoldsByProjectId(parentId);
      setRelatedMolds(molds);
      
      // 각 금형의 파일 개수 및 파일 목록 조회
      const counts: Record<string, number> = {};
      const filesMap: Record<string, EnhancedProjectFile[]> = {};
      
      await Promise.all(
        molds.map(async (mold) => {
          if (mold.id) {
            const files = await getMoldFiles(mold.id);
            counts[mold.id] = files.length;
            filesMap[mold.id] = files as EnhancedProjectFile[];
          }
        })
      );
      
      setMoldFileCounts(counts);
      setMoldFiles(filesMap);
    } catch (error) {
      console.error('관련 금형 로드 실패:', error);
    } finally {
      setLoadingMolds(false);
    }
  };

  /**
   * 금형 파일 표시 토글
   */
  const handleToggleMoldFiles = useCallback(() => {
    setShowMoldFiles(prev => !prev);
  }, []);

  // ===== Computed Values =====

  /**
   * 업로더 목록 (중복 제거)
   * @memoized files, users 변경 시에만 재계산
   */
  const availableUploaders = useMemo(() => {
    const uploaderIds = new Set<string>();
    files.forEach((file) => {
      if (file.uploadedBy) {
        uploaderIds.add(file.uploadedBy);
      }
    });
    
    // ID를 사용자 정보로 매핑
    const uploaderList: UploaderInfo[] = [];
    uploaderIds.forEach(id => {
      const user = users.find(u => u.id === id);
      if (user) {
        uploaderList.push(user);
      } else {
        // 사용자 정보를 못 찾으면 ID만 표시
        uploaderList.push({
          id,
          name: id,
          email: '',
        });
      }
    });
    
    // 이름 기준으로 정렬
    return uploaderList.sort((a, b) => a.name.localeCompare(b.name));
  }, [files, users]);

  /**
   * 필터링된 파일 목록 (검색 + 필터 적용)
   * @memoized files, searchTerm, filters 변경 시에만 재계산
   * @complexity O(n) - 전체 파일 목록을 순회하며 필터 적용
   */
  const filteredFiles = useMemo(() => {
    let result = [...files];

    // 검색 필터
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((file) => {
        const fileName = file.fileName?.toLowerCase() || '';
        const description = file.description?.toLowerCase() || '';
        const tags = (file.tags || []).map((tag: string) => tag.toLowerCase());
        const keywords = (file.searchKeywords || []).map((kw: string) => kw.toLowerCase());

        return (
          fileName.includes(term) ||
          description.includes(term) ||
          tags.some((tag) => tag.includes(term)) ||
          keywords.some((kw) => kw.includes(term))
        );
      });
    }

    // 카테고리 필터
    if (filters.category !== '전체') {
      result = result.filter((file) => file.category === filters.category);
    }

    // 업로더 필터
    if (filters.uploader) {
      result = result.filter((file) => file.uploadedBy === filters.uploader);
    }

    // 크기 범위 필터
    if (filters.sizeRange) {
      const { min, max } = filters.sizeRange;
      result = result.filter((file) => file.fileSize >= min && file.fileSize <= max);
    }

    // 태그 필터 (AND 조건)
    if (filters.tags.length > 0) {
      result = result.filter((file) =>
        filters.tags.every((tag) => (file.tags || []).includes(tag))
      );
    }

    // 즐겨찾기 필터
    if (filters.onlyFavorites) {
      result = result.filter((file) => file.isFavorite === true);
    }

    return result;
  }, [files, searchTerm, filters]);

  /**
   * 활성화된 필터 개수
   * @memoized filters 변경 시에만 재계산
   * @returns 활성 필터 개수 (0-5)
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category !== '전체') count++;
    if (filters.uploader) count++;
    if (filters.sizeRange) count++;
    if (filters.tags.length > 0) count++;
    if (filters.onlyFavorites) count++;
    return count;
  }, [filters]);

  const handleGoBack = () => {
    if (parentType === 'project') {
      navigate(`/projects/${parentId}`);
    } else {
      navigate(`/molds/${parentId}`);
    }
  };

  // ===== 필터 핸들러 =====

  const handleFilterChange = useCallback((newFilters: FileFilters) => {
    setFilters(newFilters);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters(getDefaultFilters());
  }, []);

  // ===== Toolbar 핸들러 =====

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleUploadClick = useCallback(() => {
    setShowUpload(true);
  }, []);

  const handleUploadComplete = useCallback((uploadedFiles: ProjectFile[]) => {
    // 업로드된 파일을 목록에 추가 (타입 변환)
    const enhancedFiles = uploadedFiles as EnhancedProjectFile[];
    setFiles((prev) => [...enhancedFiles, ...prev]);
    // setShowUpload(false); 제거 - FileUploadZone에서 확인 버튼 클릭 시 닫힘
  }, []);

  const handleUploadClose = useCallback(() => {
    setShowUpload(false);
  }, []);

  const handleFilterClick = useCallback(() => {
    setShowFilter((prev) => !prev);
  }, []);

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((fileId: string, selected: boolean) => {
    setSelectedFiles((prev) =>
      selected ? [...prev, fileId] : prev.filter((id) => id !== fileId)
    );
  }, []);

  // 파일 클릭 핸들러 - 상세 패널 열기
  const handleFileClick = useCallback((file: EnhancedProjectFile) => {
    setSelectedFile(file);
  }, []);

  // 상세 패널 닫기
  const handleDetailPanelClose = useCallback(() => {
    setSelectedFile(null);
  }, []);

  // 파일 정보 업데이트
  const handleUpdateFileInfo = useCallback(async (fileId: string, data: FileUpdateData) => {
    if (!parentId) return;

    try {
      await updateFileInfo(parentType, parentId, fileId, data);
      
      // 로컬 상태 업데이트
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, ...data } : f
        )
      );

      // 선택된 파일도 업데이트
      if (selectedFile && selectedFile.id === fileId) {
        setSelectedFile((prev) => prev ? { ...prev, ...data } : null);
      }

      console.log('파일 정보 업데이트 완료:', fileId);
    } catch (error) {
      console.error('파일 정보 업데이트 실패:', error);
      throw error;
    }
  }, [parentType, parentId, selectedFile]);

  // 파일 다운로드 핸들러
  const handleFileDownload = useCallback(async (file: EnhancedProjectFile) => {
    if (!file.fileUrl && !file.storagePath) {
      alert('다운로드할 수 없는 파일입니다.');
      return;
    }

    try {
      if (!file.fileUrl) {
        throw new Error('파일 URL을 찾을 수 없습니다.');
      }

      // fetch로 파일 다운로드 후 원본 파일명으로 저장
      const response = await fetch(file.fileUrl);
      if (!response.ok) {
        throw new Error('파일 다운로드에 실패했습니다.');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // 다운로드 링크 생성
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.fileName; // 원본 파일명 사용
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Blob URL 해제
      URL.revokeObjectURL(blobUrl);
      
      console.log('파일 다운로드 완료:', file.fileName);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      alert(`파일 다운로드에 실패했습니다.\n${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  }, []);

  // 파일 삭제 핸들러
  const handleFileDelete = useCallback(async (file: EnhancedProjectFile) => {
    if (!file.id || !parentId) return;

    // 삭제 확인
    const confirmed = window.confirm(
      `"${file.fileName}" 파일을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
    );

    if (!confirmed) return;

    try {
      // 파일 삭제
      if (parentType === 'project') {
        await deleteProjectFile(parentId, file.id);
      } else {
        await deleteMoldFile(parentId, file.id);
      }

      // 로컬 상태에서 제거
      setFiles((prev) => prev.filter((f) => f.id !== file.id));

      // 선택 목록에서도 제거
      setSelectedFiles((prev) => prev.filter((id) => id !== file.id));

      // 상세 패널이 열려있으면 닫기
      if (selectedFile && selectedFile.id === file.id) {
        setSelectedFile(null);
      }

      console.log('파일 삭제 완료:', file.fileName);
    } catch (error) {
      console.error('파일 삭제 실패:', error);
      alert('파일 삭제에 실패했습니다.');
    }
  }, [parentType, parentId, selectedFile]);

  // 파일 즐겨찾기 토글 핸들러
  const handleToggleFavorite = useCallback(async (file: EnhancedProjectFile) => {
    if (!file.id || !parentId) return;

    const newFavoriteStatus = !file.isFavorite;

    try {
      // Firestore 업데이트
      await toggleFileFavorite(parentType, parentId, file.id, newFavoriteStatus);

      // 로컬 상태 업데이트
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, isFavorite: newFavoriteStatus } : f
        )
      );

      // 선택된 파일도 업데이트
      if (selectedFile && selectedFile.id === file.id) {
        setSelectedFile((prev) => prev ? { ...prev, isFavorite: newFavoriteStatus } : null);
      }

      console.log('즐겨찾기 업데이트:', file.fileName, newFavoriteStatus);
    } catch (error) {
      console.error('즐겨찾기 업데이트 실패:', error);
      alert('즐겨찾기 업데이트에 실패했습니다.');
    }
  }, [parentType, parentId, selectedFile]);

  // 일괄 다운로드 핸들러 (ZIP 압축)
  const handleBulkDownload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    // 선택된 파일 객체 찾기
    const filesToDownload = files.filter((f) => f.id && selectedFiles.includes(f.id));

    if (filesToDownload.length === 0) {
      alert('다운로드할 파일을 선택해주세요.');
      return;
    }

    // 다운로드 확인
    const confirmed = window.confirm(
      `선택한 ${filesToDownload.length}개 파일을 ZIP으로 압축하여 다운로드하시겠습니까?`
    );

    if (!confirmed) return;

    try {
      // JSZip 인스턴스 생성
      const zip = new JSZip();
      let successCount = 0;
      let failCount = 0;

      // 각 파일을 ZIP에 추가
      for (const file of filesToDownload) {
        try {
          // fileUrl을 사용하여 파일 다운로드
          if (!file.fileUrl) {
            throw new Error('파일 URL을 찾을 수 없음');
          }

          const response = await fetch(file.fileUrl);
          if (!response.ok) {
            throw new Error('파일 다운로드 실패');
          }
          const blob = await response.blob();

          // ZIP에 파일 추가 (원본 파일명 유지)
          zip.file(file.fileName, blob);
          successCount++;
        } catch (error) {
          console.error(`파일 추가 실패 (${file.fileName}):`, error);
          failCount++;
        }
      }

      // ZIP 파일 생성 및 다운로드
      if (successCount > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // ZIP 파일명 생성 (날짜 포함)
        const today = new Date();
        const dateString = today.toISOString().split('T')[0]; // YYYY-MM-DD
        const zipFileName = `files_${dateString}_${successCount}개.zip`;
        
        // ZIP 다운로드
        const blobUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = zipFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        // 결과 알림
        if (failCount === 0) {
          alert(`${successCount}개 파일이 ZIP으로 다운로드되었습니다.`);
        } else {
          alert(`${successCount}개 성공, ${failCount}개 실패\nZIP 파일로 다운로드되었습니다.`);
        }
      } else {
        alert('다운로드할 파일이 없습니다.');
      }

      // 선택 초기화
      setSelectedFiles([]);
    } catch (error) {
      console.error('ZIP 다운로드 실패:', error);
      alert('ZIP 파일 생성에 실패했습니다.');
    }
  }, [selectedFiles, files]);

  // 일괄 삭제 핸들러
  const handleBulkDelete = useCallback(async () => {
    if (selectedFiles.length === 0 || !parentId) return;

    // 선택된 파일 객체 찾기
    const filesToDelete = files.filter((f) => f.id && selectedFiles.includes(f.id));

    if (filesToDelete.length === 0) {
      alert('삭제할 파일을 선택해주세요.');
      return;
    }

    // 삭제 확인
    const confirmed = window.confirm(
      `선택한 ${filesToDelete.length}개 파일을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
    );

    if (!confirmed) return;

    // 순차적으로 삭제
    let successCount = 0;
    let failCount = 0;
    const deletedIds: string[] = [];

    for (const file of filesToDelete) {
      if (!file.id) continue;

      try {
        if (parentType === 'project') {
          await deleteProjectFile(parentId, file.id);
        } else {
          await deleteMoldFile(parentId, file.id);
        }
        
        deletedIds.push(file.id);
        successCount++;
      } catch (error) {
        console.error(`파일 삭제 실패 (${file.fileName}):`, error);
        failCount++;
      }
    }

    // 로컬 상태에서 제거
    if (deletedIds.length > 0) {
      setFiles((prev) => prev.filter((f) => !f.id || !deletedIds.includes(f.id)));
    }

    // 결과 알림
    if (failCount === 0) {
      alert(`${successCount}개 파일 삭제가 완료되었습니다.`);
    } else {
      alert(`${successCount}개 성공, ${failCount}개 실패`);
    }

    // 선택 초기화
    setSelectedFiles([]);
  }, [selectedFiles, files, parentType, parentId]);

  // 로딩 상태
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* 헤더 */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={handleGoBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          파일 관리 ({filteredFiles.length}개
          {files.length !== filteredFiles.length && ` / 전체 ${files.length}개`})
        </Typography>
      </Box>

      {/* Toolbar */}
      <FileToolbar
        totalFiles={filteredFiles.length}
        selectedCount={selectedFiles.length}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onSearch={handleSearch}
        onUploadClick={handleUploadClick}
        onFilterClick={handleFilterClick}
        onBulkDownload={handleBulkDownload}
        onBulkDelete={handleBulkDelete}
        activeFilterCount={activeFilterCount}
      />

      {/* 금형 파일 표시 옵션 (프로젝트 타입일 때만) */}
      {parentType === 'project' && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showMoldFiles}
                onChange={handleToggleMoldFiles}
                color="primary"
              />
            }
            label="관련 금형 파일 표시"
          />
        </Box>
      )}
      
      {/* 메인 레이아웃 */}
      <Box display="flex" gap={2}>
        {/* 좌측: 파일 뷰 */}
        <Box flex={showFilter ? "7" : "1"} minWidth={0}>
          {filteredFiles.length === 0 ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {files.length === 0 ? (
                  '업로드된 파일이 없습니다.'
                ) : (
                  <>
                    검색 또는 필터 조건에 맞는 파일이 없습니다.
                    {activeFilterCount > 0 && (
                      <Box component="span" sx={{ ml: 1, fontWeight: 'bold' }}>
                        (활성 필터: {activeFilterCount}개)
                      </Box>
                    )}
                  </>
                )}
              </Alert>
              {files.length > 0 && (searchTerm || activeFilterCount > 0) && (
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      handleFilterReset();
                    }}
                  >
                    검색 및 필터 초기화
                  </Button>
                </Box>
              )}
            </Box>
          ) : viewMode === 'grid' ? (
            <FileGridView
              files={filteredFiles}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
              onFileEdit={handleFileClick}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
              onToggleFavorite={handleToggleFavorite}
              searchTerm={searchTerm}
            />
          ) : viewMode === 'list' ? (
            <FileListView
              files={filteredFiles}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
              onFileClick={handleFileClick}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
              searchTerm={searchTerm}
            />
          ) : viewMode === 'timeline' ? (
            <FileTimelineView
              files={filteredFiles}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
              onFileClick={handleFileClick}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
              searchTerm={searchTerm}
            />
          ) : (
            <Typography>알 수 없는 뷰 모드</Typography>
          )}

          {/* 관련 금형 파일 섹션 (프로젝트 타입 + 체크 ON + 금형 있음) */}
          {parentType === 'project' && showMoldFiles && relatedMolds.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📦 관련 금형 파일
                <Chip label={`${relatedMolds.length}개 금형`} size="small" color="primary" />
              </Typography>

              {loadingMolds ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {relatedMolds.map((mold) => (
                    <Accordion key={mold.id}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {mold.moldNumber} - {mold.moldName}
                          </Typography>
                          <Chip 
                            label={`${moldFileCounts[mold.id!] || 0}개 파일`} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          금형 파일은 읽기 전용입니다. 편집하려면 금형 상세 페이지로 이동하세요.
                        </Alert>
                        
                        {/* 파일 목록 */}
                        {moldFiles[mold.id!] && moldFiles[mold.id!].length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                            {moldFiles[mold.id!].map((file) => (
                              <Box 
                                key={file.id}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 2,
                                  p: 1.5,
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  📄 {file.fileName}
                                </Typography>
                                <Chip label={file.category} size="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(file.fileSize)}
                                </Typography>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleFileDownload(file)}
                                  title="다운로드"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            파일이 없습니다.
                          </Typography>
                        )}
                        
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/molds/${mold.id}`)}
                        >
                          금형 상세 페이지로 이동
                        </Button>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* 우측: 필터 패널 (조건부 표시) */}
        {showFilter && (
          <Box flex="3" minWidth={280}>
            <FileFilterPanel
              filters={filters}
              onChange={handleFilterChange}
              availableUploaders={availableUploaders}
              onReset={handleFilterReset}
            />
          </Box>
        )}
      </Box>

      {/* 파일 업로드 다이얼로그 */}
      {parentId && (
        <FileUploadZone
          parentType={parentType}
          parentId={parentId}
          parentName={parentName}
          open={showUpload}
          onClose={handleUploadClose}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* 파일 상세 정보 패널 */}
      <FileDetailPanel
        file={selectedFile}
        open={!!selectedFile}
        onClose={handleDetailPanelClose}
        onUpdate={handleUpdateFileInfo}
        onDownload={handleFileDownload}
        onDelete={async (fileId: string) => {
          // FileDetailPanel에서 confirm을 처리하므로 여기서는 바로 삭제
          const file = files.find(f => f.id === fileId);
          if (!file || !file.id || !parentId) return;

          // Firestore에서 삭제
          if (parentType === 'project') {
            await deleteProjectFile(parentId, file.id);
          } else {
            await deleteMoldFile(parentId, file.id);
          }

          // 로컬 상태 업데이트
          setFiles((prev) => prev.filter((f) => f.id !== file.id));
          setSelectedFiles((prev) => prev.filter((id) => id !== file.id));
          
          // 상세 패널 닫기
          if (selectedFile && selectedFile.id === file.id) {
            setSelectedFile(null);
          }
        }}
        onToggleFavorite={async (fileId: string, isFavorite: boolean) => {
          const file = files.find(f => f.id === fileId);
          if (file) {
            await handleToggleFavorite(file);
          }
        }}
      />
    </Container>
  );
};

export default FileManagerPage;
